import { useCallback, useEffect } from 'react'
import { intl } from '../../../../i18n'
import {
  ProjectClosingStatusSearchCondition,
  useOnSingleValueChangedGenerator,
} from '../hooks/useSearchCondition'
import { Button } from '../../../components/buttons'
import SearchConditionSelectBoxCheckmarks from '../../../components/search-forms/search-panel/SearchConditionSelectBoxCheckmarks'
import { ProjectStatus } from '../../../../lib/functions/project'
import { SelectOption } from '../../../model/selectOption'
import { Box, Dialog, DialogContent, Tooltip, Typography } from '@mui/material'

const dialogStyles = {
  zIndex: 1200,
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: '120px',
  },
  '& .MuiPaper-root': {
    maxWidth: '600px',
    width: '90%',
    margin: '0 auto',
    overflow: 'visible',
  },
  '& .MuiPopper-root': {
    zIndex: 1300,
    minWidth: '400px !important',
    maxWidth: '400px !important',
    width: '400px !important',
  },
}

const contentBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '20px',
  minHeight: '200px',
}

const titleStyles = {
  marginBottom: '10px',
  fontWeight: 600,
}

const selectBoxWrapperStyles = {
  width: '100%',
  maxWidth: '500px',
}

const buttonContainerStyles = {
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
  paddingTop: '10px',
  marginTop: '20px',
  position: 'relative',
}

const dialogContentStyles = {
  overflow: 'visible',
}

type Props = {
  open: boolean
  onClose: () => void
  searchCondition: ProjectClosingStatusSearchCondition
  onChangeCondition: (
    updated: Partial<ProjectClosingStatusSearchCondition>
  ) => void
  resetCondition: () => void
  search: () => void
  projectCount?: number
}

const ProjectClosingStatusSearchConditionDialog = ({
  open,
  onClose,
  searchCondition,
  onChangeCondition,
  resetCondition,
  search,
  projectCount,
}: Props) => {
  const { onChangeValueGenerator } = useOnSingleValueChangedGenerator(
    searchCondition,
    onChangeCondition
  )

  const onSearch = useCallback(() => {
    search()
    onClose()
  }, [search, onClose])

  const projectStatusOptions: SelectOption[] = [
    {
      value: ProjectStatus.STANDBY,
      name: 'STANDBY',
    },
    {
      value: ProjectStatus.INPROGRESS,
      name: 'INPROGRESS',
    },
    {
      value: ProjectStatus.SUSPENDED,
      name: 'SUSPENDED',
    },
    {
      value: ProjectStatus.COMPLETED,
      name: 'COMPLETED',
    },
  ]

  const closingStatusOptions: SelectOption[] = [
    {
      value: 'true',
      name: intl.formatMessage({ id: 'projectClosingStatus.closed' }),
    },
    {
      value: 'false',
      name: intl.formatMessage({ id: 'projectClosingStatus.open' }),
    },
  ]

  const isSearchable = true

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      sx={dialogStyles}
    >
      <DialogContent sx={dialogContentStyles}>
        <Box sx={contentBoxStyles}>
          <Typography variant="h6" sx={titleStyles}>
            {intl.formatMessage({ id: 'projectClosingStatus.detailSearch' })}
          </Typography>
          <Box sx={selectBoxWrapperStyles}>
            <SearchConditionSelectBoxCheckmarks
              title={intl.formatMessage({
                id: 'projectClosingStatus.projectStatus',
              })}
              value={searchCondition.projectStatus || []}
              options={projectStatusOptions}
              onChange={onChangeValueGenerator('projectStatus')}
            />
          </Box>
          <Box sx={selectBoxWrapperStyles}>
            <SearchConditionSelectBoxCheckmarks
              title={intl.formatMessage({
                id: 'projectClosingStatus.closingStatus',
              })}
              value={searchCondition.closingStatus || []}
              options={closingStatusOptions}
              onChange={onChangeValueGenerator('closingStatus')}
            />
          </Box>
          <Box sx={buttonContainerStyles}>
            {projectCount !== undefined && (
              <Typography
                variant="body2"
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: 1,
                  position: 'absolute',
                  top: '-25px',
                }}
              >
                {projectCount}件
              </Typography>
            )}
            <Button
              size="m"
              colorPattern="monotone"
              variant="outlined"
              onClick={resetCondition}
            >
              <Typography>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.reset' })}
              </Typography>
            </Button>
            <Tooltip
              title={
                !isSearchable
                  ? intl.formatMessage({ id: 'search.disabled.tooltip' })
                  : ''
              }
            >
              <span>
                <Button
                  size="m"
                  colorPattern="skyBlue"
                  variant="filled"
                  onClick={onSearch}
                  disabled={!isSearchable}
                >
                  <Typography>
                    {intl.formatMessage({
                      id: 'wbsItemSearchFilterPanel.search',
                    })}
                  </Typography>
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ProjectClosingStatusSearchConditionDialog
